import {
  DEFAULT_SESSION_TARGET_PATH,
  SESSION_ACTION_TYPES,
  SESSION_BLOCKING_REASONS,
  SESSION_PHASES,
} from "./constants";
import {
  clearSessionBlockingState,
  createInitialSessionOrchestratorState,
  normalizeBlockingReason,
  normalizeSessionTargetPath,
} from "./state";

export const sessionActionCreators = Object.freeze({
  loginStarted: ({ returnTo } = {}) => ({
    type: SESSION_ACTION_TYPES.loginStarted,
    payload: { returnTo },
  }),
  callbackStarted: ({ returnTo } = {}) => ({
    type: SESSION_ACTION_TYPES.callbackStarted,
    payload: { returnTo },
  }),
  callbackTokensReady: ({ targetPath } = {}) => ({
    type: SESSION_ACTION_TYPES.callbackTokensReady,
    payload: { targetPath },
  }),
  profileBootstrapStarted: ({ targetPath } = {}) => ({
    type: SESSION_ACTION_TYPES.profileBootstrapStarted,
    payload: { targetPath },
  }),
  profileBootstrapResolved: ({
    targetPath,
    requiresVerification = false,
  } = {}) => ({
    type: SESSION_ACTION_TYPES.profileBootstrapResolved,
    payload: { targetPath, requiresVerification },
  }),
  recoveryRequested: ({
    blockingReason,
    targetPath,
    recoveryContext = null,
  } = {}) => ({
    type: SESSION_ACTION_TYPES.recoveryRequested,
    payload: { blockingReason, targetPath, recoveryContext },
  }),
  bootstrapRetried: ({ targetPath } = {}) => ({
    type: SESSION_ACTION_TYPES.bootstrapRetried,
    payload: { targetPath },
  }),
  hardErrorRaised: ({ blockingReason, errorCode = "", targetPath } = {}) => ({
    type: SESSION_ACTION_TYPES.hardErrorRaised,
    payload: { blockingReason, errorCode, targetPath },
  }),
  errorAcknowledged: () => ({
    type: SESSION_ACTION_TYPES.errorAcknowledged,
  }),
  verificationRequired: ({ targetPath } = {}) => ({
    type: SESSION_ACTION_TYPES.verificationRequired,
    payload: { targetPath },
  }),
});

const nextRequestId = (state) => Math.max(1, Number(state?.requestId || 0) + 1);

const resolveRequestIdOnCallback = (state) =>
  Math.max(1, Number(state?.requestId || 0));

export const sessionOrchestratorReducer = (
  state = createInitialSessionOrchestratorState(),
  action = {}
) => {
  const current = createInitialSessionOrchestratorState(state);
  const { type, payload = {} } = action || {};

  switch (type) {
    case SESSION_ACTION_TYPES.loginStarted:
      return clearSessionBlockingState({
        ...current,
        phase: SESSION_PHASES.redirecting,
        targetPath: normalizeSessionTargetPath(
          payload.returnTo,
          DEFAULT_SESSION_TARGET_PATH
        ),
        requestId: nextRequestId(current),
      });

    case SESSION_ACTION_TYPES.callbackStarted:
      return clearSessionBlockingState({
        ...current,
        phase: SESSION_PHASES.callbackProcessing,
        targetPath: normalizeSessionTargetPath(
          payload.returnTo,
          current.targetPath || DEFAULT_SESSION_TARGET_PATH
        ),
        requestId: resolveRequestIdOnCallback(current),
      });

    case SESSION_ACTION_TYPES.callbackTokensReady:
    case SESSION_ACTION_TYPES.profileBootstrapStarted:
    case SESSION_ACTION_TYPES.bootstrapRetried:
      return clearSessionBlockingState({
        ...current,
        phase: SESSION_PHASES.bootstrappingProfile,
        targetPath: normalizeSessionTargetPath(
          payload.targetPath,
          current.targetPath || DEFAULT_SESSION_TARGET_PATH
        ),
      });

    case SESSION_ACTION_TYPES.profileBootstrapResolved: {
      const requiresVerification = payload.requiresVerification === true;
      return {
        ...clearSessionBlockingState(current),
        phase: requiresVerification
          ? SESSION_PHASES.verificationRequired
          : SESSION_PHASES.ready,
        blockingReason: requiresVerification
          ? SESSION_BLOCKING_REASONS.emailUnverified
          : null,
        targetPath: normalizeSessionTargetPath(
          payload.targetPath,
          current.targetPath || DEFAULT_SESSION_TARGET_PATH
        ),
      };
    }

    case SESSION_ACTION_TYPES.verificationRequired:
      return {
        ...clearSessionBlockingState(current),
        phase: SESSION_PHASES.verificationRequired,
        blockingReason: SESSION_BLOCKING_REASONS.emailUnverified,
        targetPath: normalizeSessionTargetPath(
          payload.targetPath,
          current.targetPath || DEFAULT_SESSION_TARGET_PATH
        ),
      };

    case SESSION_ACTION_TYPES.recoveryRequested:
      return {
        ...current,
        phase: SESSION_PHASES.recoveryRequired,
        blockingReason: normalizeBlockingReason(payload.blockingReason),
        targetPath: normalizeSessionTargetPath(
          payload.targetPath,
          current.targetPath || DEFAULT_SESSION_TARGET_PATH
        ),
        errorCode: "",
        recoveryContext:
          typeof payload.recoveryContext === "string" && payload.recoveryContext.trim()
            ? payload.recoveryContext.trim()
            : null,
      };

    case SESSION_ACTION_TYPES.hardErrorRaised:
      return {
        ...current,
        phase: SESSION_PHASES.hardError,
        blockingReason: normalizeBlockingReason(payload.blockingReason),
        targetPath: normalizeSessionTargetPath(
          payload.targetPath,
          current.targetPath || DEFAULT_SESSION_TARGET_PATH
        ),
        errorCode: typeof payload.errorCode === "string" ? payload.errorCode.trim() : "",
      };

    case SESSION_ACTION_TYPES.errorAcknowledged:
      return createInitialSessionOrchestratorState({
        requestId: current.requestId,
      });

    default:
      return current;
  }
};
